import { getNestedOrFloatingElementById, batchCreatePerson, createHighlightsDispatches } from '@fs/zion-sls'

/**
 * 1) Get the list of highlights that are being edited
 * 2) Get the field from which the highlight is being removed and the appropriate shared element
 * 3) For each person who previously had the shared element as its subelement, create a dispatch to remove
 * the old field that had the highlight and another dispatch to add as a subelement the new field that took the highlight
 * @param {array} images The array of images from the zion sls state
 * @param {object} types The types object from the zion sls state
 * @param {string} currentImageArk The ark of the current image, from zion sls state
 * @param {object} highlights The highlights object from h
 * @param {string} newGivenNameId The id of the given name field on the new person
 * @param {string} newSurnameId The id of the surname field on the new person
 * @param {string} newSexFieldId The id of the sex field on the new person
 * @returns {Array} dispatches The array of dispatches to reassign the subelements as explained in the description above
 */
export function reassignHighlightDispatches({
  images,
  types,
  currentImageArk,
  highlights,
  newGivenNameId,
  newSurnameId,
  newSexFieldId,
}) {
  const dispatches = []
  const highlightEdits = highlights.mainEditHighlights.filter((highlight) => highlight.change === 'edit')
  highlightEdits.forEach((edit) => {
    // Check destination field to make sure it is a shareable type (not NAME_GIVEN)
    const typesField = types.fields.find((type) => type.fieldType === edit.fieldType)
    const fieldIsShareable = !typesField?.singleSuper
    const elementIsShareable = !types.elements.find((type) => type.elementType === typesField.elementTypes[0])
      ?.singleSuper
    if (fieldIsShareable || elementIsShareable) {
      const fieldBeingEdited = getNestedOrFloatingElementById(images, edit.originalHighlight.fieldId)
      let sharedElement = fieldBeingEdited
      if (sharedElement.typeData?.singleSuper && fieldBeingEdited?.superElements)
        sharedElement = getNestedOrFloatingElementById(images, fieldBeingEdited?.superElements[0]?.id)
      if (sharedElement?.superElements) {
        sharedElement.superElements.forEach((superElement) => {
          const parentOfSharedElement = getNestedOrFloatingElementById(images, superElement.id)
          let subId

          switch (sharedElement.fieldType) {
            case 'NAME_SURN':
              subId = newSurnameId
              break
            case 'NAME_GN':
              // I don't think this one is used, as given name is not shareable
              subId = newGivenNameId
              break
            case 'SEX_CODE':
              // Not used since there's no add highlight button for sex field
              subId = newSexFieldId
              break
            default:
              subId = null
              break
          }
          if (edit.personIds?.includes(parentOfSharedElement.personId)) {
            dispatches.push({
              type: 'REM_SUB',
              value: {
                superPartitionKey: parentOfSharedElement.partitionKey,
                subPartitionKey: sharedElement.partitionKey,
                superId: parentOfSharedElement.id,
                subId: sharedElement.id,
                elementType: parentOfSharedElement.elementType,
              },
            })
            dispatches.push({
              type: 'ADD_SUB',
              value: {
                superPartitionKey: parentOfSharedElement.partitionKey,
                subPartitionKey: currentImageArk,
                superId: parentOfSharedElement.id,
                subId,
                elementType: parentOfSharedElement.elementType,
              },
            })
          }
        })
      }
    }
  })
  return dispatches
}

/**
 * Prepare dispatches to add person. Returns an object with the full array of dispatches, as well as broken up into sub arrays.
 * @param {object} batchPersonParams Object will get passed to batchCreatePerson to create elements
 * @param {array} images The array of images from the zion sls state
 * @param {string} currentImageArk The ark of the current image, from zion sls state
 * @param {object} recordType The selected option for the record type input by user
 * @param {object} types The types object from the zion sls state
 * @param {object} highlights The highlights object from h
 * @param {boolean} [skipRecordCreation=false] If true, the dispatches to create a new record will be skipped.
 * @returns {{personDispatches: T[], nameDispatches: T[], fullArray: *[], recordDispatches: [{type: string, value: {extraArgs: {recordType: *}, partitionKey, id, elementType: string}},{type: string, value: {subId, subPartitionKey, superId, superPartitionKey, elementType: string}}], setIdsDispatches: *}}
 */
export function prepareDispatches({
  batchPersonParams,
  images,
  currentImageArk,
  recordType,
  types,
  highlights,
  skipRecordCreation = false,
}) {
  const recordDispatches = skipRecordCreation
    ? []
    : [
        {
          type: 'CREATE_ELEMENT',
          value: {
            elementType: 'RECORD',
            partitionKey: batchPersonParams.partitionKey,
            id: batchPersonParams.recordId,
            extraArgs: { recordType: recordType?.key },
          },
        },
        // This action is for our reducer.  Records are floating in SLS, so this action won't be sent to SLS
        {
          type: 'ADD_SUB',
          value: {
            superPartitionKey: batchPersonParams.partitionKey,
            subPartitionKey: batchPersonParams.partitionKey,
            superId: batchPersonParams.partitionKey,
            subId: batchPersonParams.recordId,
            elementType: 'IMAGE',
          },
        },
      ]
  // CREATE_ELEMENT for PERSON and ADD_SUB to RECORD will be pulled out of this array later
  let nameDispatches = batchCreatePerson(batchPersonParams)

  // Add highlights dispatches
  nameDispatches = nameDispatches.concat(
    createHighlightsDispatches(images, currentImageArk, highlights.mainEditHighlights)
  )
  nameDispatches = nameDispatches.concat(
    createHighlightsDispatches(images, currentImageArk, highlights.splitScreenEditHighlights)
  )
  const newGivenNameId = nameDispatches.find(
    (d) => d.type === 'CREATE_ELEMENT' && d?.value?.extraArgs?.fieldType === 'NAME_GN'
  )?.value?.id
  const newSurnameId = nameDispatches.find(
    (d) => d.type === 'CREATE_ELEMENT' && d?.value?.extraArgs?.fieldType === 'NAME_SURN'
  )?.value?.id
  const newSexFieldId = nameDispatches.find(
    (d) => d.type === 'CREATE_ELEMENT' && d?.value?.extraArgs?.fieldType === 'SEX_CODE'
  )?.value?.id
  nameDispatches = nameDispatches.concat(
    reassignHighlightDispatches({
      images,
      types,
      currentImageArk,
      highlights,
      newGivenNameId,
      newSurnameId,
      newSexFieldId,
    })
  )

  // Subdivide nameDispatches by putting personDispatches in its own array
  const createPersonDispIx = nameDispatches.findIndex(
    (disp) => disp?.type === 'CREATE_ELEMENT' && disp?.value?.elementType === 'PERSON'
  )
  const personDispatches = nameDispatches.splice(createPersonDispIx, 1)
  const addSubToRecordDispIx = nameDispatches.findIndex(
    (disp) => disp?.type === 'ADD_SUB' && disp?.value?.elementType === 'RECORD'
  )
  personDispatches.push(...nameDispatches.splice(addSubToRecordDispIx, 1))

  // We need to be sure the new shared fields in state have the correct record and person ids
  const setIdsDispatches = images.reduce((acc, image) => {
    if (image.subElements) image.subElements.forEach((se) => acc.push({ type: 'SET_RECORD_PERSON_IDS', value: se?.id }))
    return acc
  }, [])
  setIdsDispatches.push({ type: 'SET_RECORD_PERSON_IDS', value: batchPersonParams.recordId })

  return {
    fullArray: [...recordDispatches, ...personDispatches, ...nameDispatches, ...setIdsDispatches],
    recordDispatches,
    personDispatches,
    nameDispatches,
    setIdsDispatches,
  }
}
